import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { Dialog, Snackbar } from '@core/material';
import { LIBRARY } from '@app/evaluation/library';
import { Crop, Library } from '@library';
import {
    CustomStorageRegimeStage,
    SampleActionTemplate,
    SampleActionTemplateType,
    ScheduledActionTemplate,
    ScheduledActionTemplateType,
    StorageRegime,
    StorageRegimeStage,
} from '@core/data';
import { addDuration, parseDuration } from '@core/utils';
import {
    actionTypeLabel,
    evaluationsFromStorageRegime,
    isCustomStage,
    scheduledActionsFromStorageRegime,
    validateStageReferences,
    validateStorageRegimeStages,
} from '../sample-form/storage-regime-schedule';

export interface StorageRegimePreviewRow {
    label: string;
    type: 'Evaluation' | 'Scheduled Action' | 'Sample Action';
    date: string | null;
    detail: string;
}

export interface StorageRegimeFormDialogData {
    ownerOrgKey: string;
    // present when editing an existing Custom Regime; absent when creating one
    regime?: Partial<StorageRegime>;
    // every regime visible to this org (Legacy + the org's own Custom
    // Regimes) — offered as "start from an existing regime" on create only
    availableRegimes: StorageRegime[];
}

let stageKeyCounter = 0;

// A short, collision-safe-enough client-generated key — only needs to be
// unique within this one regime's own stage list, never across regimes or
// persisted servers, since it only ever exists inside a single config blob.
function nextStageKey(): string {
    return `stage-${Date.now()}-${stageKeyCounter++}`;
}

// Matches StorageRegime.DEFAULT_COLOR on the server, so a regime without a colour looks the same in both places.
const DEFAULT_COLOUR = '#FFFFFF';

@Component({
    selector: 'pv-storage-regime-form-dialog',
    templateUrl: 'storage-regime-form.dialog.html',
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'pv-storage-regime-form-dialog pv-fullscreen-dialog',
    },
})
export class StorageRegimeFormDialog implements OnInit, OnDestroy {

    readonly isEditing: boolean = !!(this._data.regime && this._data.regime.key);

    // create-only — picking one clones its config into the form below, at
    // creation time, never a live reference back to the source regime
    sourceRegimeControl = new FormControl(null);

    readonly scheduledActionTypes: ScheduledActionTemplateType[] = ['move', 'packout', 'treatment'];

    formGroup = new FormGroup({
        label: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
        description: new FormControl(null),
        overdueThresholdDays: new FormControl(null, [Validators.min(0)]),
        cropId: new FormControl(null, [Validators.required]),
        stages: new FormArray([]),
        scheduledActions: new FormArray([]),
        sampleActions: new FormArray([]),
    });

    private _destroy$ = new Subject();

    get stages(): FormArray {
        return this.formGroup.get('stages') as FormArray;
    }

    get scheduledActions(): FormArray {
        return this.formGroup.get('scheduledActions') as FormArray;
    }

    get sampleActions(): FormArray {
        return this.formGroup.get('sampleActions') as FormArray;
    }

    // The Stage-card editing list — kept in authoring/insertion order so adding a new Stage
    // always appends visually, regardless of its offset relative to existing Stages. Chronological
    // order (sortedStages) is reserved for things that must read as a real timeline, e.g. the
    // "Due relative to stage" picker.
    get stageEntries(): { control: FormGroup; index: number }[] {
        return this.stages.controls.map((control: FormGroup, index) => ({ control, index }));
    }

    // Chronological order, for anywhere Stages must read as an actual timeline rather than
    // authoring order — never re-sorts the underlying FormArray, which would change removeStage(index)'s index meaning.
    get sortedStages(): { control: FormGroup; index: number }[] {
        return this.stageEntries
            .slice()
            .sort((a, b) => this.stageOffsetDays(a.control.value) - this.stageOffsetDays(b.control.value));
    }

    private stageOffsetDays(stage: Partial<CustomStorageRegimeStage>): number {
        const multiplier = stage.offsetUnit === 'weeks' ? 7 : 1;
        return (stage.offsetValue ?? 0) * multiplier;
    }

    // A Destroy sample-action is only ever relative to the regime's final Stage, never an
    // earlier one — destroying mid-regime at a fixed offset from a non-final Stage isn't a
    // real scenario this form supports, so the picker only ever offers this single option.
    get lastStage(): { control: FormGroup; index: number } | null {
        const sorted = this.sortedStages;
        return sorted.length > 0 ? sorted[sorted.length - 1] : null;
    }

    // "Add Scheduled Action" is disabled until at least one Stage exists.
    get hasStages(): boolean {
        return this.stages.length > 0;
    }

    scheduledActionsForStage(stageKey: string): { control: FormGroup; index: number }[] {
        return this.scheduledActions.controls
            .map((control: FormGroup, index) => ({ control, index }))
            .filter(entry => entry.control.get('precedesStageKey').value === stageKey);
    }

    // trackBy for the Stage/Scheduled-Action *ngFors — sortedStages()/scheduledActionsForStage() return a fresh wrapper every call, so track by the stable FormGroup inside it, not the wrapper.
    trackByControl(index: number, entry: { control: FormGroup }): FormGroup {
        return entry.control;
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) public _data: StorageRegimeFormDialogData,
        private _ref: MatDialogRef<StorageRegimeFormDialog>,
        private _snackbar: Snackbar,
        private _dialog: Dialog,
        @Inject(LIBRARY) private _library: Library,
    ) {
    }

    cropOptions: Crop[] = this._library.filterExcludedCrops();

    // Crops a regime was linked to before the form opened. Kept on save unless the user picks a different crop, so no link is dropped silently.
    private storedCropIds: string[] = [];

    ngOnInit() {
        const config = this._data.regime && this._data.regime.config;

        if (config) {
            this.storedCropIds = this._data.regime.cropIds ?? [];
            this.ensureCropOption(this.storedCropIds[0]);

            this.formGroup.patchValue({
                label: config.label,
                description: config.description,
                overdueThresholdDays: config.overdueThresholdDays,
                cropId: this._data.regime.cropIds?.[0] ?? null,
            });

            // Editing always operates on a Custom Regime's own already-saved config — never a Legacy Regime, which has no edit entry point.
            (config.evaluations || []).forEach((stage: CustomStorageRegimeStage) => this.addStage({ ...stage, color: stage.color ?? config.color }));
            (config.scheduledActions || []).forEach(action => this.addScheduledAction(action.precedesStageKey, action));
            (config.sampleActions || []).forEach(action => this.addSampleAction(action.type, action));
        }

        if (!this.isEditing) {
            this.sourceRegimeControl.valueChanges
                .pipe(takeUntil(this._destroy$))
                .subscribe(regimeKey => this.applySourceRegime(regimeKey));
        }

        this.formGroup.get('cropId').valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this.updateDependentControls());

        this.updateDependentControls();
    }

    // Crops come first, as in the custom protocol form: nothing else can be filled in until at least one crop is chosen.
    get hasCrops(): boolean {
        return !!this.formGroup.get('cropId').value;
    }

    private updateDependentControls() {
        const enabled = this.hasCrops;
        const dependents = ['label', 'description', 'overdueThresholdDays'];

        dependents.forEach(name => {
            const control = this.formGroup.get(name);
            enabled ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
        });

        enabled ? this.stages.enable({ emitEvent: false }) : this.stages.disable({ emitEvent: false });
        enabled ? this.scheduledActions.enable({ emitEvent: false }) : this.scheduledActions.disable({ emitEvent: false });
        enabled ? this.sampleActions.enable({ emitEvent: false }) : this.sampleActions.disable({ emitEvent: false });
        enabled ? this.sourceRegimeControl.enable({ emitEvent: false }) : this.sourceRegimeControl.disable({ emitEvent: false });
        this.clearStartFromIfNoLongerOffered();
    }

    // A crop the library no longer offers still needs an option, or the saved value would show blank.
    private ensureCropOption(cropId: string | null): void {
        if (!cropId || this.cropOptions.some(crop => crop.id === cropId)) return;

        const crop = this._library.crops.get(cropId);
        if (crop) this.cropOptions = [...this.cropOptions, crop];
    }

    // One crop is chosen in the form. If it's one of the stored crops, every stored link is kept.
    private get selectedCropIds(): string[] {
        const cropId = this.formGroup.get('cropId').value;
        if (!cropId) return [];
        return this.storedCropIds.includes(cropId) ? this.storedCropIds : [cropId];
    }

    // A regime is offered when it applies to any selected crop: custom regimes by their crop links, legacy regimes by the library's schedules for the crop.
    get startFromOptions(): StorageRegime[] {
        const cropIds = this.selectedCropIds;
        const legacyIds = new Set<string>();

        cropIds.forEach(cropId => {
            const crop = this._library.crops.get(cropId);
            (crop?.schedules || []).forEach(id => legacyIds.add(id));
        });

        return (this._data.availableRegimes || []).filter(regime => regime.isGlobal
            ? legacyIds.has(regime.config?.legacyId)
            : (regime.cropIds || []).some(cropId => cropIds.includes(cropId)));
    }

    private clearStartFromIfNoLongerOffered() {
        const selected = this.sourceRegimeControl.value;
        if (selected && !this.startFromOptions.some(regime => regime.key === selected)) {
            this.sourceRegimeControl.setValue(null, { emitEvent: false });
        }
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // Copies the chosen regime's stage skeleton (names + harvest-offsets) into this form at creation time, never a link — re-picking (including back to "start from scratch") always replaces, never merges, prior values.
    applySourceRegime(regimeKey: string) {
        this.stages.clear();

        // Only the stage skeleton is cloned — any Scheduled Action or stage-referencing Destroy authored against the stages just cleared would dangle.
        this.scheduledActions.clear();
        this.removeDestroyStageReferences();

        const source = (this._data.availableRegimes || []).find(regime => regime.key === regimeKey);

        if (source) {
            (source.config?.evaluations || []).forEach(stage => this.addStage({
                label: stage.label,
                color: stage.color ?? source.config?.color,
                ...this.sourceStageOffset(stage),
            }));
        }

        this.formGroup.patchValue({
            overdueThresholdDays: source?.config?.overdueThresholdDays ?? null,
        });
    }

    // Translates a cloned stage into the number+unit offset shape: a Custom source's own offset copies directly; a Legacy source's cumulative storage durations are resolved via real calendar arithmetic rather than a flat day-equivalent guess. Exact for day/week units — every real Legacy Library duration uses days only — since those have no calendar ambiguity regardless of anchor. A month unit cannot be made exact against an unknown future Sample (no startDate exists yet at clone time), so this is a deterministic approximation for that case, not a guaranteed match.
    private sourceStageOffset(stage: StorageRegimeStage): Pick<CustomStorageRegimeStage, 'offsetValue' | 'offsetUnit'> {
        if (isCustomStage(stage)) {
            return { offsetValue: stage.offsetValue, offsetUnit: stage.offsetUnit };
        }

        // No real Sample/startDate exists yet at clone time — anchor on a fixed, arbitrary date so the result is at least deterministic rather than varying by the day the clone happens on.
        const reference = moment('2000-01-01');
        const resolved = (stage.storage || []).reduce((date, store) => addDuration(date, parseDuration(store.duration)), reference);

        return { offsetValue: resolved.diff(reference, 'days'), offsetUnit: 'days' };
    }

    addStage(stage?: Partial<CustomStorageRegimeStage>) {
        const group = new FormGroup({
            stageKey: new FormControl((stage && stage.stageKey) || nextStageKey()),
            color: new FormControl((stage && stage.color) || DEFAULT_COLOUR, [Validators.pattern(/^#[0-9A-Fa-f]{6}$/)]),
            label: new FormControl((stage && stage.label) || null, [Validators.required]),
            offsetValue: new FormControl((stage && stage.offsetValue) ?? 0, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]),
            offsetUnit: new FormControl((stage && stage.offsetUnit) || 'days', [Validators.required]),
        });

        this.stages.push(group);
    }

    // Dependent actions must be named and confirmed before removal, never silently cascade-deleted.
    removeStage(index: number) {
        const stageKey = this.stages.at(index).get('stageKey').value;
        const dependents = this.dependentActions(stageKey);

        if (dependents.total === 0) {
            this.stages.removeAt(index);
            return;
        }

        this._dialog.confirm(
            'Remove stage',
            `This stage is used by: ${dependents.names.join(', ')}. Removing it also removes ${dependents.total === 1 ? 'that action' : 'those actions'}.`,
            'Remove',
        ).afterClosed().pipe(
            filter(confirmed => !!confirmed),
            takeUntil(this._destroy$),
        ).subscribe(() => {
            // Resolve the live index by stageKey — the closed-over `index` param may be stale if array positions shifted while this confirm dialog was open.
            const liveIndex = this.stages.controls.findIndex((control: FormGroup) => control.get('stageKey').value === stageKey);
            if (liveIndex === -1) return;

            this.scheduledActionsForStage(stageKey).slice().reverse().forEach(entry => this.scheduledActions.removeAt(entry.index));
            this.removeDestroyStageReferences(stageKey);
            this.stages.removeAt(liveIndex);
        });
    }

    // names are deduplicated by type with a count suffix ("move (x2)") instead of an ambiguous "move, move"; total is the real item count.
    private dependentActions(stageKey: string): { names: string[]; total: number } {
        const scheduledTypes = this.scheduledActionsForStage(stageKey).map(entry => entry.control.get('type').value);

        const destroyTypes = this.sampleActions.controls
            .filter((control: FormGroup) => control.get('type').value === 'destroy' && control.get('precedesStageKey').value === stageKey)
            .map(() => 'destroy');

        const allTypes = [...scheduledTypes, ...destroyTypes];
        const counts = new Map<string, number>();
        allTypes.forEach(type => counts.set(type, (counts.get(type) || 0) + 1));

        const names = Array.from(counts.entries()).map(([type, count]) => count > 1 ? `${type} (x${count})` : type);

        return { names, total: allTypes.length };
    }

    // Clears a destroy action's stage reference rather than removing the entry — destroy without a stage reference is still valid.
    private removeDestroyStageReferences(stageKey?: string) {
        this.sampleActions.controls
            .filter((control: FormGroup) => control.get('type').value === 'destroy' && (stageKey == null || control.get('precedesStageKey').value === stageKey))
            .forEach((control: FormGroup) => control.patchValue({
                precedesStageKey: null,
                offsetValue: null,
                offsetUnit: null,
                isAfter: false,
            }));
    }

    // Single source of truth for the regime's stage list — shared by save() and the preview/validation getters, so the preview can never diverge from what would actually be persisted.
    private assembleStages(): CustomStorageRegimeStage[] {
        return this.stages.value.map((stage: any) => ({
            stageKey: stage.stageKey,
            label: stage.label,
            offsetValue: stage.offsetValue,
            offsetUnit: stage.offsetUnit,
            color: stage.color || null,
        }));
    }

    addScheduledAction(precedesStageKey: string, action?: Partial<ScheduledActionTemplate>) {
        const metadata = (action && action.metadata) || {};

        const group = new FormGroup({
            precedesStageKey: new FormControl(precedesStageKey, [Validators.required]),
            type: new FormControl((action && action.type) || null, [Validators.required]),
            offsetValue: new FormControl((action && action.offsetValue) ?? 0, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]),
            offsetUnit: new FormControl((action && action.offsetUnit) || 'days', [Validators.required]),
            isAfter: new FormControl((action && action.isAfter) || false),
            // type-specific metadata, flattened for simple [formControlName] binding, assembled back into one object per type.
            moveCondition: new FormControl(metadata['condition'] ?? null),
            moveTemperature: new FormControl(metadata['temperature'] ?? null),
            treatmentApplied: new FormControl(metadata['treatment'] ?? null),
            packOutCondition: new FormControl(metadata['condition'] ?? null),
            packOutTemperature: new FormControl(metadata['temperature'] ?? null),
        });

        this.scheduledActions.push(group);
    }

    removeScheduledAction(index: number) {
        this.scheduledActions.removeAt(index);
    }

    private metadataForScheduledAction(action: any): Record<string, unknown> | null {
        switch (action.type) {
            case 'move': return { condition: action.moveCondition || null, temperature: action.moveTemperature ?? null };
            case 'treatment': return { treatment: action.treatmentApplied || null };
            case 'packout': return (action.packOutCondition || action.packOutTemperature != null)
                ? { condition: action.packOutCondition || null, temperature: action.packOutTemperature ?? null }
                : null;
            default: return null;
        }
    }

    private assembleScheduledActions(): ScheduledActionTemplate[] {
        return this.scheduledActions.value.map((action: any) => ({
            precedesStageKey: action.precedesStageKey,
            type: action.type,
            offsetValue: action.offsetValue,
            offsetUnit: action.offsetUnit,
            isAfter: !!action.isAfter,
            metadata: this.metadataForScheduledAction(action),
        }));
    }

    addSampleAction(type: SampleActionTemplateType, action?: Partial<SampleActionTemplate>) {
        const metadata = (action && action.metadata) || {};

        // Treatment's offset is always relative to harvest, required (0 is a valid "same day" value) —
        // unlike Destroy's, which stays optional (no fixed date) since a type never changes after
        // creation (fixed "Add intake/treatment/destroy" buttons, never a switchable dropdown).
        const offsetValueValidators = type === 'treatment'
            ? [Validators.required, Validators.min(0), Validators.pattern(/^\d*$/)]
            : [Validators.min(0), Validators.pattern(/^\d*$/)];

        const group = new FormGroup({
            type: new FormControl(type, [Validators.required]),
            treatmentApplied: new FormControl(metadata['treatment'] ?? null),
            // Intake's target storage condition — the Sample's starting point, mirroring a Scheduled Action's own 'move' fields.
            intakeCondition: new FormControl(metadata['condition'] ?? null),
            intakeTemperature: new FormControl(metadata['temperature'] ?? null),
            precedesStageKey: new FormControl((action && action.precedesStageKey) || null),
            offsetValue: new FormControl((action && action.offsetValue) ?? (type === 'treatment' ? 0 : null), offsetValueValidators),
            offsetUnit: new FormControl((action && action.offsetUnit) || (type === 'treatment' ? 'days' : null)),
            isAfter: new FormControl((action && action.isAfter) || false),
        });

        this.sampleActions.push(group);
    }

    removeSampleAction(index: number) {
        this.sampleActions.removeAt(index);
    }

    private metadataForSampleAction(action: any): Record<string, unknown> | null {
        switch (action.type) {
            case 'treatment': return action.treatmentApplied ? { treatment: action.treatmentApplied } : null;
            case 'intake': {
                // A malformed number input can leave the control holding NaN rather than null —
                // JSON.stringify silently turns NaN into null on the wire either way, but treating
                // it as "no value" here (not "value present") keeps the has-metadata check honest.
                const temperature = typeof action.intakeTemperature === 'number' && !isNaN(action.intakeTemperature)
                    ? action.intakeTemperature
                    : null;
                return (action.intakeCondition || temperature != null)
                    ? { condition: action.intakeCondition || null, temperature }
                    : null;
            }
            default: return null;
        }
    }

    private assembleSampleActions(): SampleActionTemplate[] {
        return this.sampleActions.value.map((action: any) => {
            const hasStageReference = action.type === 'destroy' && !!action.precedesStageKey;
            const isTreatment = action.type === 'treatment';
            const hasOffset = hasStageReference || isTreatment;
            const metadata = this.metadataForSampleAction(action);

            return {
                type: action.type,
                metadata,
                // Both a stage-referenced Destroy and a Treatment carry the same offsetValue/offsetUnit
                // shape — Destroy additionally carries precedesStageKey/isAfter (always after the final
                // Stage, never before — the form no longer exposes a before/after choice for Destroy, so
                // this is hard-coded rather than read from the now-hidden isAfter control); Treatment has
                // neither concept at all, it's always relative to harvest.
                ...(hasOffset ? { offsetValue: action.offsetValue, offsetUnit: action.offsetUnit } : {}),
                ...(hasStageReference ? { precedesStageKey: action.precedesStageKey, isAfter: true } : {}),
            };
        });
    }

    // Authoring-time graph validation, read directly from the template to drive both the save guard and the "fix errors first" preview placeholder so they can never disagree.
    get graphErrors(): string[] {
        if (this.stages.length === 0) return [];

        return [
            ...validateStorageRegimeStages(this.assembleStages()),
            ...validateStageReferences(this.assembleStages(), this.assembleScheduledActions(), this.assembleSampleActions()),
        ];
    }

    // Live schedule preview, reusing the real runtime calculation against today's date — never a second, simulated one. Empty whenever graphErrors is non-empty (never previews invalid data).
    get previewRows(): StorageRegimePreviewRow[] {
        if (this.stages.length === 0 || this.graphErrors.length > 0) return [];

        const today = moment().format('YYYY-MM-DD');
        const assembledStages = this.assembleStages();
        const assembledScheduledActions = this.assembleScheduledActions();
        const assembledSampleActions = this.assembleSampleActions();

        const regime: StorageRegime = {
            key: 'preview',
            ownerOrgKey: this._data.ownerOrgKey,
            isGlobal: false,
            config: {
                label: this.formGroup.get('label').value,
                evaluations: assembledStages,
                scheduledActions: assembledScheduledActions,
                sampleActions: assembledSampleActions,
            },
        };

        // rank is a same-day tiebreak (Intake, then "before" actions, then the stage, then "after" actions) — stripped before the rows are returned.
        const evaluationRows: (StorageRegimePreviewRow & { rank: number })[] = evaluationsFromStorageRegime('preview-sample', regime, 'preview-protocol', today, 0)
            .map((evalu, index) => ({
                label: evalu.label,
                type: 'Evaluation' as const,
                date: evalu.evalStartDate,
                detail: `${assembledStages[index].offsetValue} ${assembledStages[index].offsetUnit} after harvest`,
                rank: 2,
            }));

        // No real generated Evaluation rows to key off in a preview, so map each stageKey to itself — fine since evalKey is never shown.
        const evaluationsByStageKey: Record<string, string> = {};
        assembledStages.forEach(stage => evaluationsByStageKey[stage.stageKey] = stage.stageKey);

        const actionRows: (StorageRegimePreviewRow & { rank: number })[] = scheduledActionsFromStorageRegime('preview-sample', regime, evaluationsByStageKey, today)
            .map(action => ({
                label: action.title,
                type: action.evalKey != null ? 'Scheduled Action' as const : 'Sample Action' as const,
                date: action.dueDate ?? null,
                detail: action.dueDate
                    ? `${actionTypeLabel(action.type)}${action.evalKey ? ` (${action.isAfter ? 'after' : 'before'} stage)` : ''}`
                    : `${actionTypeLabel(action.type)} (no fixed date)`,
                rank: action.type === 'intake' ? 0 : (action.isAfter ? 3 : 1),
            }));

        // Only a stage-less Destroy ever lands here now — Treatment always resolves a real date via
        // scheduledActionsFromStorageRegime()'s own actionRows above, so it must never also appear
        // here, or it would show up twice (once correctly dated, once wrongly "no fixed date").
        const datelessRows: (StorageRegimePreviewRow & { rank: number })[] = assembledSampleActions
            .filter(action => action.type === 'destroy' && !action.precedesStageKey)
            .map(action => ({
                label: actionTypeLabel(action.type),
                type: 'Sample Action' as const,
                date: null,
                detail: `${actionTypeLabel(action.type)} (no fixed date)`,
                rank: 4,
            }));

        // Rows with no date sort last — localeCompare('', x) would otherwise put them first, reading as "due immediately" instead of "no due date".
        const sortKey = (row: { date: string | null }) => row.date || '￿';

        return [...evaluationRows, ...actionRows, ...datelessRows]
            .sort((a, b) => sortKey(a).localeCompare(sortKey(b)) || a.rank - b.rank)
            .map(({ rank, ...row }) => row);
    }

    // trackBy for the preview table — previewRows rebuilds fresh row objects every read, so *ngFor needs identity by content, not reference.
    trackByPreviewRow(index: number, row: StorageRegimePreviewRow): string {
        return `${row.type}:${row.label}:${row.date}`;
    }

    save() {
        this.formGroup.updateValueAndValidity();

        if (this.stages.length === 0 || this.formGroup.invalid) {
            // markAllAsTouched() recurses through every nested FormGroup/FormArray — without it,
            // an untouched invalid field (e.g. a never-focused required Label) shows no red
            // mat-error at all, leaving the generic snackbar as the only clue something's wrong.
            this.formGroup.markAllAsTouched();
            this._snackbar.formInvalid();
            return;
        }

        const errors = this.graphErrors;

        if (errors.length > 0) {
            this._snackbar.error(errors.join(' '));
            return;
        }

        const form = this.formGroup.value;

        const payload: Partial<StorageRegime> = {
            key: this._data.regime && this._data.regime.key,
            ownerOrgKey: this._data.ownerOrgKey,
            config: {
                label: form.label,
                description: form.description,
                overdueThresholdDays: form.overdueThresholdDays,
                evaluations: this.assembleStages(),
                scheduledActions: this.assembleScheduledActions(),
                sampleActions: this.assembleSampleActions(),
            },
            cropIds: this.selectedCropIds,
        };

        this._ref.close(payload);
    }

    cancel() {
        this._ref.close();
    }
}
